/**
 * Authenticated reservation detail (`/reservations/:id`). Mirrors the mobile
 * app: shows the booking, lets the user pay an unpaid reservation via Pix
 * (QR Code + copy-and-paste, polling until confirmed) and cancel with a
 * refund preview.
 *
 * Compliance: the Pix payment block carries the Asaas seal + processed-by
 * clause, as required by the BaaS rulebook on every payer-facing surface.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { analyticsService } from '@meiuca/analytics'
import { ANALYTICS_EVENTS, SCREEN_NAMES } from '@/constants/analyticsEvents'
import {
  AsaasSeal,
  Body,
  Button,
  ButtonStyle,
  ButtonVariant,
  Caption,
  H4,
  Modal
} from '@meiuca/ui-components'
import {
  AlertCircle,
  ArrowLeft,
  Calendar,
  CheckCircle2,
  Clock,
  Loader2,
  MapPin,
  XCircle,
  type LucideIcon
} from 'lucide-react'
import { NotFoundError } from '@meiuca/service'
import { formatCurrency, formatDateForUI, formatTimeForUI, parseAPIDate } from '@meiuca/web-foundation'
import { MarketplaceHeader } from '@/components/MarketplaceHeader'
import { PixPayment } from '@/components/PixPayment'
import { useAuth } from '@/contexts/AuthContext'
import { toastManager } from '@/lib/toastManager'
import {
  reservationService,
  type AppReservationDetail,
  type ReservationCancelPreview
} from '@/services/reservationService'

const POLL_INTERVAL_MS = 4000

type LoadState =
  | { kind: 'loading' }
  | { kind: 'not-found' }
  | { kind: 'error' }
  | { kind: 'ok'; reservation: AppReservationDetail }

type StatusTone = 'positive' | 'warning' | 'negative' | 'neutral'

// Design-system semantic status intents (positive / negative / warning).
const TONE_STYLES: Record<StatusTone, { wrap: string; text: string; Icon: LucideIcon }> = {
  positive: { wrap: 'bg-status-positive-bg', text: 'text-status-positive-text', Icon: CheckCircle2 },
  warning: { wrap: 'bg-status-warning-bg', text: 'text-status-warning-text', Icon: Clock },
  negative: { wrap: 'bg-status-negative-bg', text: 'text-status-negative-text', Icon: AlertCircle },
  neutral: { wrap: 'bg-background-secondary', text: 'text-text-secondary', Icon: XCircle }
}

/** Single, prominent status combining reservation + payment state. */
function resolveReservationStatus(
  reservation: AppReservationDetail
): { label: string; subtitle?: string; tone: StatusTone } {
  if (reservation.status === 'canceled') {
    // A canceled reservation can still have money in flight — the refund is
    // requested at cancellation but only settles later on the gateway.
    if (reservation.paymentStatus === 'refundProcessing') {
      return {
        label: 'Reserva cancelada · estorno em processamento',
        subtitle: 'O valor será devolvido na conta usada no pagamento em até 1 dia útil.',
        tone: 'warning'
      }
    }
    if (reservation.paymentStatus === 'refunded') {
      return { label: 'Reserva cancelada · valor reembolsado', tone: 'neutral' }
    }
    return { label: 'Reserva cancelada', tone: 'neutral' }
  }
  switch (reservation.paymentStatus) {
    case 'paid':
      return { label: 'Pagamento confirmado', subtitle: 'Sua reserva está paga e garantida.', tone: 'positive' }
    case 'processing':
      return { label: 'Processando pagamento', subtitle: 'Aguarde a confirmação.', tone: 'warning' }
    case 'failed':
      return { label: 'Pagamento não concluído', subtitle: 'Tente novamente para garantir sua reserva.', tone: 'negative' }
    case 'chargeback':
      return { label: 'Pagamento contestado', tone: 'negative' }
    case 'refunded':
      return { label: 'Pagamento reembolsado', tone: 'neutral' }
    default:
      return {
        label: 'Aguardando pagamento',
        subtitle: 'Conclua o pagamento para garantir sua reserva.',
        tone: 'warning'
      }
  }
}

export function ReservationDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()

  const autoPayTriggeredRef = useRef(false)

  const [state, setState] = useState<LoadState>({ kind: 'loading' })

  // Pix payment flow
  const [showPix, setShowPix] = useState(false)
  const [pixLoading, setPixLoading] = useState(false)
  const [pix, setPix] = useState<{ payload: string; qrCodeBase64: string } | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Cancel flow
  const [cancelOpen, setCancelOpen] = useState(false)
  const [cancelPreview, setCancelPreview] = useState<ReservationCancelPreview | null>(null)
  const [cancelLoading, setCancelLoading] = useState(false)
  const [canceling, setCanceling] = useState(false)

  useEffect(() => {
    document.title = 'Reserva · Meiuca'
    analyticsService.trackScreenView(SCREEN_NAMES.RESERVATION_DETAIL)
  }, [])

  const loadDetail = useCallback(async () => {
    if (!id) {
      setState({ kind: 'not-found' })
      return
    }
    try {
      const reservation = await reservationService.getReservationDetail(id)
      setState({ kind: 'ok', reservation })
    } catch (err) {
      if (err instanceof NotFoundError) {
        setState({ kind: 'not-found' })
      } else {
        setState({ kind: 'error' })
      }
    }
  }, [id])

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }
    loadDetail()
  }, [isAuthenticated, loadDetail, navigate])

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [])

  // Cleanup polling on unmount.
  useEffect(() => stopPolling, [stopPolling])

  // `silent` = auto-attempt on load (no toasts, don't force the Pix UI open when
  // there's no Pix — e.g. pay-at-venue reservations).
  const handlePay = async (opts?: { silent?: boolean }) => {
    if (!id) return
    setShowPix(true)
    setPixLoading(true)
    try {
      const details = await reservationService.getReservationPaymentDetails(id)
      const payload = details.paymentDetails?.pix?.payload
      const encodedImage = details.paymentDetails?.pix?.encodedImage
      if (payload && encodedImage) {
        setPix({ payload, qrCodeBase64: encodedImage })
        analyticsService.track(ANALYTICS_EVENTS.RESERVATION_PIX_OPENED, {
          reservation_id: id,
          source: opts?.silent ? 'auto' : 'manual',
        })
        startPolling()
      } else {
        setPix(null)
        if (opts?.silent) {
          setShowPix(false)
        } else {
          toastManager.info('Estamos preparando o seu Pix. Tente novamente em instantes.')
        }
      }
    } catch {
      if (!opts?.silent) {
        toastManager.error('Não conseguimos carregar o Pix. Tente novamente.')
      }
      setShowPix(false)
    } finally {
      setPixLoading(false)
    }
  }

  const startPolling = () => {
    stopPolling()
    pollRef.current = setInterval(async () => {
      if (!id) return
      try {
        const details = await reservationService.getReservationPaymentDetails(id)
        if (details.paymentStatus === 'paid') {
          stopPolling()
          setShowPix(false)
          setPix(null)
          analyticsService.track(ANALYTICS_EVENTS.RESERVATION_PIX_CONFIRMED, { reservation_id: id })
          toastManager.success('Pagamento confirmado!')
          loadDetail()
        } else if (details.paymentStatus === 'failed') {
          // Terminal on the server side: the charge was canceled (payment
          // expired or admin action) and the reservation is gone with it.
          stopPolling()
          setShowPix(false)
          setPix(null)
          toastManager.info('O pagamento não foi concluído e a reserva foi cancelada.')
          loadDetail()
        }
      } catch {
        // Transient error while polling — keep trying on the next tick.
      }
    }, POLL_INTERVAL_MS)
  }

  const openCancel = async () => {
    if (!id) return
    analyticsService.track(ANALYTICS_EVENTS.RESERVATION_CANCEL_OPENED, { reservation_id: id })
    setCancelOpen(true)
    setCancelLoading(true)
    setCancelPreview(null)
    try {
      const preview = await reservationService.getCancelPreview(id)
      setCancelPreview(preview)
    } catch {
      toastManager.error('Não conseguimos carregar as condições de cancelamento.')
      setCancelOpen(false)
    } finally {
      setCancelLoading(false)
    }
  }

  const confirmCancel = async () => {
    if (!id) return
    setCanceling(true)
    try {
      await reservationService.cancelReservation(id)
      analyticsService.track(ANALYTICS_EVENTS.RESERVATION_CANCELLED, {
        reservation_id: id,
        refund_amount_cents: cancelPreview?.resolution.refundAmountCents,
      })
      stopPolling()
      setShowPix(false)
      setCancelOpen(false)
      toastManager.success('Reserva cancelada.')
      loadDetail()
    } catch {
      toastManager.error('Não conseguimos cancelar a reserva. Tente novamente.')
    } finally {
      setCanceling(false)
    }
  }

  // Whenever the reservation is open, auto-load the Pix and keep polling until
  // it's paid (mirrors the mobile detail; covers both fresh bookings and
  // returning to a reservation with an open Pix). Silent for pay-at-venue.
  useEffect(() => {
    if (autoPayTriggeredRef.current) return
    if (state.kind !== 'ok') return
    const { status, paymentStatus } = state.reservation
    if (status === 'canceled' || paymentStatus === 'paid') return
    autoPayTriggeredRef.current = true
    handlePay({ silent: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state])

  return (
    <div className="min-h-screen bg-background-primary">
      <MarketplaceHeader />
      <div className="container mx-auto px-4 py-6 max-w-2xl">
        <button
          onClick={() => navigate('/reservations')}
          className="flex items-center gap-2 text-text-secondary hover:text-text-primary mb-4 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Minhas reservas
        </button>

        {state.kind === 'loading' && (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="w-10 h-10 text-brand-primary animate-spin" />
            <Body className="text-text-secondary mt-4">Carregando reserva...</Body>
          </div>
        )}

        {state.kind === 'not-found' && (
          <EmptyState
            title="Reserva não encontrada"
            message="O link pode estar incorreto ou a reserva não está mais disponível."
          />
        )}

        {state.kind === 'error' && (
          <EmptyState
            title="Algo deu errado"
            message="Não conseguimos carregar esta reserva agora."
            onRetry={loadDetail}
          />
        )}

        {state.kind === 'ok' && (
          <ReservationView
            reservation={state.reservation}
            showPix={showPix}
            pixLoading={pixLoading}
            pix={pix}
            onCancel={openCancel}
          />
        )}
      </div>

      <CancelModal
        open={cancelOpen}
        loading={cancelLoading}
        preview={cancelPreview}
        canceling={canceling}
        onClose={() => {
          analyticsService.track(ANALYTICS_EVENTS.RESERVATION_CANCEL_ABORTED, { reservation_id: id })
          setCancelOpen(false)
        }}
        onConfirm={confirmCancel}
      />
    </div>
  )
}

function EmptyState({
  title,
  message,
  onRetry
}: {
  title: string
  message: string
  onRetry?: () => void
}) {
  return (
    <div className="rounded-2xl bg-background-white p-8 text-center shadow-sm border border-border-light">
      <H4 className="mb-2 text-text-primary">{title}</H4>
      <Body className="text-text-secondary">{message}</Body>
      {onRetry && (
        <div className="mt-4 flex justify-center">
          <Button variant={ButtonVariant.PRIMARY} onClick={onRetry}>
            Tentar novamente
          </Button>
        </div>
      )}
    </div>
  )
}

function ReservationView({
  reservation,
  showPix,
  pixLoading,
  pix,
  onCancel
}: {
  reservation: AppReservationDetail
  showPix: boolean
  pixLoading: boolean
  pix: { payload: string; qrCodeBase64: string } | null
  onCancel: () => void
}) {
  const isCanceled = reservation.status === 'canceled'
  const isPaid = reservation.paymentStatus === 'paid'
  const canPay = !isCanceled && !isPaid
  const canCancel = !isCanceled
  const status = resolveReservationStatus(reservation)
  const tone = TONE_STYLES[status.tone]

  const mapsLink =
    reservation.venue.latitude && reservation.venue.longitude
      ? `https://www.google.com/maps/search/?api=1&query=${reservation.venue.latitude},${reservation.venue.longitude}`
      : reservation.venue.address
        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(reservation.venue.address)}`
        : null

  return (
    <div className="space-y-4">
      {/* Venue card */}
      <div className="rounded-2xl bg-background-white p-5 shadow-sm border border-border-light space-y-3">
        <H4 className="text-text-primary">{reservation.venue.name}</H4>

        {reservation.venue.address && (
          <Caption className="text-text-secondary flex items-start gap-1">
            <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{reservation.venue.address}</span>
          </Caption>
        )}

        <div className="flex items-center gap-2 text-text-secondary">
          <Calendar className="w-4 h-4" />
          <Body className="text-sm">{formatDateForUI(parseAPIDate(reservation.date), 'long')}</Body>
        </div>

        {mapsLink && (
          <a
            href={mapsLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm text-brand-primary hover:underline"
          >
            <MapPin className="w-4 h-4" />
            Ver no mapa
          </a>
        )}
      </div>

      {/* Time groups / courts */}
      {reservation.timeGroups.length > 0 && (
        <div className="rounded-2xl bg-background-white p-5 shadow-sm border border-border-light">
          <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wide mb-3">
            Horários
          </h2>
          <div className="space-y-3">
            {reservation.timeGroups.map((group, i) => (
              <div key={i} className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-text-tertiary mt-1 flex-shrink-0" />
                <div>
                  <Body className="text-sm font-medium text-text-primary">
                    {formatTimeForUI(parseAPIDate(group.startsAt))} —{' '}
                    {formatTimeForUI(parseAPIDate(group.endsAt))}
                  </Body>
                  <Caption className="text-text-secondary">
                    {group.courts.map((c) => c.name).join(', ')}
                  </Caption>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pagamento — total, status and (when open) the Pix, all grouped together. */}
      <div className="rounded-2xl bg-background-white p-5 shadow-sm border border-border-light space-y-4">
        <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wide">
          Pagamento
        </h2>

        <div className="flex items-center justify-between">
          <Body className="text-text-secondary m-0">Total</Body>
          <Body className="text-2xl font-bold text-brand-primary m-0">
            {formatCurrency(reservation.priceCents)}
          </Body>
        </div>

        {/* Payment status, right next to the value */}
        <div className={`flex items-start gap-2 rounded-xl px-3 py-2.5 ${tone.wrap}`}>
          <tone.Icon className={`w-5 h-5 flex-shrink-0 ${tone.text}`} />
          <div className="min-w-0">
            <Body className={`text-sm font-semibold m-0 ${tone.text}`}>{status.label}</Body>
            {status.subtitle && (
              <Caption className="text-text-secondary">{status.subtitle}</Caption>
            )}
          </div>
        </div>

        {/* Open Pix — auto-loaded, polling until confirmed. Method is chosen at booking. */}
        {canPay && showPix && (
          <div className="space-y-4 pt-2 border-t border-border-light">
            {pixLoading ? (
              <div className="flex flex-col items-center py-6">
                <Loader2 className="w-8 h-8 text-brand-primary animate-spin" />
                <Caption className="text-text-secondary mt-3">Gerando o Pix...</Caption>
              </div>
            ) : pix ? (
              <>
                <PixPayment payload={pix.payload} qrCodeBase64={pix.qrCodeBase64} />
                <Caption className="text-text-tertiary text-center">
                  Assim que o pagamento for confirmado, esta tela é atualizada automaticamente.
                </Caption>
                <div className="flex flex-col items-center gap-2 pt-2 border-t border-border-light">
                  <AsaasSeal size="sm" />
                  <Caption className="text-text-tertiary">Pagamento processado por Asaas IP</Caption>
                </div>
              </>
            ) : null}
          </div>
        )}
      </div>

      {/* Cancel */}
      {canCancel && (
        <Button variant={ButtonVariant.NEGATIVE} onClick={onCancel} className="w-full justify-center">
          Cancelar reserva
        </Button>
      )}
    </div>
  )
}

function CancelModal({
  open,
  loading,
  preview,
  canceling,
  onClose,
  onConfirm
}: {
  open: boolean
  loading: boolean
  preview: ReservationCancelPreview | null
  canceling: boolean
  onClose: () => void
  onConfirm: () => void
}) {
  const mustContact = preview?.resolution.kind === 'mustContactArena'

  const resolutionMessage = (() => {
    if (!preview) return ''
    const { kind, refundAmountCents, cancellationFeeCents } = preview.resolution
    switch (kind) {
      case 'canCancelNoRefundDue':
        return 'Você pode cancelar esta reserva. Não há reembolso a receber.'
      case 'canCancelAutomaticOnlineRefund':
        return `Você receberá ${formatCurrency(refundAmountCents ?? 0)} de volta via Pix${
          cancellationFeeCents && cancellationFeeCents > 0
            ? ` (taxa de cancelamento de ${formatCurrency(cancellationFeeCents)})`
            : ''
        }.`
      case 'canCancelManualFullRefund':
        return `A arena devolverá ${formatCurrency(refundAmountCents ?? 0)} manualmente após o cancelamento.`
      case 'mustContactArena':
        return 'Para cancelar esta reserva e alinhar o reembolso, entre em contato diretamente com a arena.'
      default:
        return 'Deseja realmente cancelar esta reserva?'
    }
  })()

  return (
    <Modal open={open} onClose={onClose} title="Cancelar reserva" size="sm">
      {loading ? (
        <div className="flex flex-col items-center py-6">
          <Loader2 className="w-8 h-8 text-brand-primary animate-spin" />
          <Caption className="text-text-secondary mt-3">Carregando condições...</Caption>
        </div>
      ) : (
        <div className="space-y-5">
          <Body className="text-text-secondary">{resolutionMessage}</Body>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              variant={ButtonVariant.SECONDARY}
              buttonStyle={ButtonStyle.CLEAR}
              onClick={onClose}
              className="w-full justify-center"
            >
              Voltar
            </Button>
            {!mustContact && (
              <Button
                variant={ButtonVariant.NEGATIVE}
                onClick={onConfirm}
                disabled={canceling}
                className="w-full justify-center"
              >
                {canceling ? 'Cancelando...' : 'Cancelar reserva'}
              </Button>
            )}
          </div>
        </div>
      )}
    </Modal>
  )
}
